import axios from "axios";
import { toast } from "react-toastify";
import { retrieveToken } from "../utils/cryptoStorage";

// Environment variable for API URL
const API_URL = import.meta.env.VITE_API_URL_NOTIFICATION;

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor to add Authorization token to each request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = retrieveToken("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 403 errors and network errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 || error.response?.status === 401) {
      localStorage.clear();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  }
);

// Centralized error handling for other status codes
const handleError = (error) => {
  if (error.isAxiosError && error.response) {
    const { statusCode, message } = error.response.data;

    if (message) {
      toast.error(message);
    }
    switch (statusCode) {
      case 500:
        throw new Error(
          "Server Error (500): The server encountered an issue. Please try again later."
        );
      case 422:
        throw new Error(
          "Validation Error (422): Invalid or incomplete data provided."
        );
      case 401:
        localStorage.clear();
        window.location.href = "/login";
        throw new Error(
          "Unauthorized (401): Access is denied. Please check your login status."
        );
      default:
        throw new Error(`API Error: Received unexpected status code ${status}`);
    }
  } else {
    throw new Error(error.message || "An unexpected error occurred.");
  }
};

// API method to add a notification
export const addNotification = async (notificationData) => {
  try {
    const response = await axiosInstance.post(
      "/notification-admin/v1",
      notificationData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// API method to duplicate a notification
export const duplicateNotification = async (notificationData) => {
  try {
    const response = await axiosInstance.post(
      "/notification-duplicate/v1",
      notificationData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// API method to delete a notification
export const deleteNotification = async (notificationData) => {
  try {
    const response = await axiosInstance.delete("/notification-admin/v1", {
      data: notificationData,
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// API method to update a notification
export const updateNotification = async (notificationData) => {
  try {
    const response = await axiosInstance.put(
      "/notification-admin/v1",
      notificationData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// API method to fetch notifications
export const fetchNotifications = async (
  page,
  perPage,
  status,
  scheduledFlg,
  title,
  alertDate,
  expiryDate,
  wardCode,
  notificationType
) => {
  try {
    let url = `/notification-admin/v1?page=${page}&perPage=${perPage}`;

    // Simplified parameter checks using optional chaining
    if (status !== undefined) url += `&status=${status}`;
    if (notificationType !== null)
      url += `&notificationType=${notificationType}`;
    if (scheduledFlg !== null) url += `&scheduledFlg=${scheduledFlg}`;
    if (alertDate !== null) url += `&alertDate=${alertDate}`;
    if (expiryDate !== null) url += `&expiryDate=${expiryDate}`;
    if (wardCode?.length > 0) url += `&wardCode=${wardCode}`;
    if (title?.length > 0) url += `&title=${encodeURIComponent(title)}`;

    const response = await axiosInstance.get(url);
    return response.data.data;
  } catch (error) {
    handleError(error);
  }
};

export const updateNotificationMedia = async (notificationData) => {
  try {
    const response = await axiosInstance.put(
      "/notification/notification-media-update/v1",
      notificationData
    );
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

export { notificationStatusUpdate } from "./notificationApprovalManagement";
